import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import styles from "./EventsSection.module.css";
import * as eventService from "../../../../services/eventService";
import { 
  doctordashboardprofile01, 
  doctordashboardprofile02, 
  doctordashboardprofile3 
} from "../../../imagepath";

import { toast } from "react-toastify";

const EventsSection = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeShareMenu, setActiveShareMenu] = useState(null);
  const navigate = useNavigate();
  const REACT_APP_EVENTS_PUBLIC_URL = process.env.REACT_APP_EVENTS_PUBLIC_URL || "https://events.devworkone.com/public/events";

  useEffect(() => {
    const fetchPublicEvents = async () => {
      try {
        setLoading(true);
        const response = await eventService.getPublicEvents();
        if (response && response.success) {
          setEvents(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        console.error("Error fetching public events:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPublicEvents();

    // Close share menu on click outside
    const handleClickOutside = () => setActiveShareMenu(null);
    window.addEventListener("click", handleClickOutside);
    return () => window.removeEventListener("click", handleClickOutside);
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const weekday = date.toLocaleDateString("default", { weekday: "long" });
    const month = date.toLocaleDateString("default", { month: "long" });
    const day = date.getDate();
    const year = date.getFullYear();
    return `${weekday} ${month} ${day} ${year}`;
  };

  const getPriceDisplay = (event) => {
    const tiers = event.ticketTiers;
    if (!tiers || tiers.length === 0) return "Free Entry";
    const minPrice = Math.min(...tiers.map((t) => t.price || 0));
    if (minPrice === 0) return "Free Access";
    return `From ${event.currency || 'KES'} ${minPrice.toLocaleString()}`;
  };

  const handleShare = (e, event, type = 'native') => {
    e.stopPropagation();
    const publicBaseUrl = process.env.REACT_APP_EVENTS_PUBLIC_URL || "https://events.devworkone.com/public/events";
    const url = `${publicBaseUrl}/${event._id || event.id}`;
    const text = `Check out this event: ${event.title}`;

    switch (type) {
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(text + " " + url)}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard!");
        break;
      default:
        if (navigator.share) {
          navigator.share({ title: event.title, text: text, url: url }).catch(console.error);
        } else {
          navigator.clipboard.writeText(url);
          toast.success("Link copied to clipboard!");
        }
    }
    setActiveShareMenu(null);
  };

  const toggleShareMenu = (e, eventId) => {
    e.stopPropagation();
    setActiveShareMenu(activeShareMenu === eventId ? null : eventId);
  };

  if (loading) {
    return (
      <section className={styles.eventsSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <div className={styles.headerLeft}>
              <h2>Upcoming Events</h2>
              <p>Discover and book tickets for the most exclusive events</p>
            </div>
          </div>
          <div className={styles.loadingGrid}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className={styles.skeletonCard}></div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.eventsSection}>
      <div className={styles.container}>
        <div className={styles.sectionHeader}>
          <div className={styles.headerLeft}>
            <h2>Upcoming Events</h2>
            <p>Discover and book tickets for the most exclusive events</p>
          </div>
          <Link to="/all-events" className={styles.viewAllBtn}>
            view more <i className="fa-solid fa-arrow-right"></i>
          </Link>
        </div>

        {events.length > 0 ? (
          <div className={styles.eventGrid}>
            {events.slice(0, 6).map((event) => {
              const dateDisplay = formatDate(event.startDate);
              const priceDisplay = getPriceDisplay(event);

              return (
                <div
                  key={event._id || event.id}
                  className={styles.eventCard}
                  onClick={() =>
                    window.open(
                      `${REACT_APP_EVENTS_PUBLIC_URL}/${event._id || event.id}`,
                      "_blank",
                    )
                  }
                >
                  <div className={styles.imageWrapper}>
                    <img
                      src={
                        event.image ||
                        "https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?auto=format&fit=crop&q=80&w=800"
                      }
                      alt={event.title}
                      className={styles.eventImage}
                    />
                    
                    <div className={styles.badgeContainer}>
                      <div className={styles.categoryBadge}>
                        <span className={styles.dot}></span>
                        {event.category || "Event"}
                      </div>
                    </div>

                    <div className={styles.actionButton}>
                      <button 
                        className={styles.shareButton}
                        onClick={(e) => toggleShareMenu(e, event._id || event.id)}
                      >
                        <i className="fa-solid fa-share-nodes"></i>
                      </button>

                      <div className={`${styles.shareMenu} ${activeShareMenu === (event._id || event.id) ? styles.shareMenuVisible : ""}`}>
                        <button className={styles.shareOption} onClick={(e) => handleShare(e, event, 'copy')}>
                          <i className="fa-regular fa-copy"></i> Copy Link
                        </button>
                        <button className={styles.shareOption} onClick={(e) => handleShare(e, event, 'whatsapp')}>
                          <i className="fa-brands fa-whatsapp" style={{ color: '#25D366' }}></i> WhatsApp
                        </button>
                        <button className={styles.shareOption} onClick={(e) => handleShare(e, event, 'facebook')}>
                          <i className="fa-brands fa-facebook" style={{ color: '#1877F2' }}></i> Facebook
                        </button>
                        <button className={styles.shareOption} onClick={(e) => handleShare(e, event, 'twitter')}>
                          <i className="fa-brands fa-x-twitter"></i> Twitter
                        </button>
                      </div>
                    </div>

                    <div className={styles.imageOverlay}></div>
                  </div>

                  <div className={styles.cardBody}>
                    <div className={styles.titleSection}>
                      <div className={styles.titleRow}>
                        <h3 className={styles.eventTitle}>{event.title}</h3>
                        <div className={styles.arrowIcon}>
                          <i className="fa-solid fa-arrow-up-right"></i>
                        </div>
                      </div>

                      <p className={styles.eventDescription}>
                        {event.description || "Join us for an unforgettable experience filled with excitement and discovery."}
                      </p>

                      <div className={styles.metaRow}>
                        <div className={styles.metaItem}>
                          <i className="fa-regular fa-calendar"></i>
                          <span>{dateDisplay}</span>
                        </div>
                        <div className={styles.metaItem}>
                          <i className="fa-solid fa-location-dot"></i>
                          <span>{event.location || event.virtualPlatform || "Online"}</span>
                        </div>
                      </div>
                    </div>

                    <div className={styles.cardFooter}>
                      <div className={styles.footerLeft}>
                        <div className={styles.registrationFlow}>
                          <span className={styles.regLabel}>Pricing</span>
                          <span className={styles.regValue}>{priceDisplay}</span>
                        </div>
                        
                        <div className={styles.avatars}>
                          <div className={styles.avatar}>
                            <img src={doctordashboardprofile01} alt="" />
                          </div>
                          <div className={styles.avatar}>
                            <img src={doctordashboardprofile02} alt="" />
                          </div>
                          <div className={styles.avatar}>
                            <img src={doctordashboardprofile3} alt="" />
                          </div>
                          <div className={styles.moreAvatars}>
                            +12
                          </div>
                        </div>
                      </div>
                      
                      <button 
                        className={styles.buyTicketBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(`${REACT_APP_EVENTS_PUBLIC_URL}/${event._id || event.id}`, '_blank');
                        }}
                      >
                        Buy Ticket
                      </button>
                    </div>

                    <div className={styles.progressAccent}></div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <i className="fa-solid fa-calendar-xmark"></i>
            <h3>No events scheduled at the moment</h3>
            <p className="text-muted mt-2">
              Check back later for exciting upcoming events!
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default EventsSection;
